import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function StaffingLeaveApplyPanel({ token, embedded = false }) {
  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'casual', from_date: '', to_date: '', reason: '' });
  const [odForm, setOdForm] = useState({ from_date: '', to_date: '', location: '', purpose: '' });
  const [history, setHistory] = useState({ leave: [], outdoor: [] });

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const load = useCallback(async () => {
    if (!API_URL || !token) return;
    try {
      const cRes = await fetch(`${API_URL}/api/candidate/staffing/context`, { headers });
      const cData = await cRes.json();
      if (!cData.hasStaffing && !cData.active) {
        setCtx(null);
        return;
      }
      setCtx(cData);
      const [lRes, oRes] = await Promise.all([
        fetch(`${API_URL}/api/candidate/staffing/leave-requests`, { headers }),
        fetch(`${API_URL}/api/candidate/staffing/outdoor-duty-requests`, { headers })
      ]);
      const lData = await lRes.json();
      const oData = await oRes.json();
      setHistory({ leave: lData.requests || [], outdoor: oData.requests || [] });
    } catch {
      setCtx(null);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const submitLeave = async () => {
    setFeedback('');
    try {
      const res = await fetch(`${API_URL}/api/candidate/staffing/leave-requests`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...leaveForm,
          frontend_origin: typeof window !== 'undefined' ? window.location.origin : ''
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit leave');
      setFeedback(data.message || 'Leave submitted.');
      setLeaveForm((f) => ({ ...f, from_date: '', to_date: '', reason: '' }));
      await load();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const submitOutdoor = async () => {
    setFeedback('');
    try {
      const res = await fetch(`${API_URL}/api/candidate/staffing/outdoor-duty-requests`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...odForm,
          frontend_origin: typeof window !== 'undefined' ? window.location.origin : ''
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit outdoor duty');
      setFeedback(data.message || 'Outdoor duty submitted.');
      setOdForm({ from_date: '', to_date: '', location: '', purpose: '' });
      await load();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  if (!ctx || !ctx.active) return embedded ? null : null;

  const inner = (
    <>
        {!embedded && (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Leave &amp; outdoor duty — {ctx.company_name}
            </Typography>
            {ctx.manager_name && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Reporting manager: {ctx.manager_name}
              </Typography>
            )}
          </>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {['casual', 'sick', 'other'].map((t) => (
            <Chip
              key={t}
              size="small"
              label={`${t}: ${ctx.balances?.[t]?.available ?? 0} left`}
              variant="outlined"
            />
          ))}
        </Stack>
        {ctx.leave_policy_document?.download_url && (
          <Alert severity="success" variant="outlined" sx={{ mb: 2, bgcolor: '#f0fdf4' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="body2" fontWeight={600}>Company leave policy</Typography>
                <Typography variant="caption" color="text.secondary">
                  {ctx.leave_policy_document.original_filename}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<PdfIcon />}
                sx={{ bgcolor: '#0d9488' }}
                onClick={async () => {
                  try {
                    const downloadUrl = ctx.leave_policy_document.download_url.startsWith('http')
                      ? ctx.leave_policy_document.download_url
                      : `${API_URL}${ctx.leave_policy_document.download_url}`;
                    const res = await fetch(downloadUrl, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (!res.ok) throw new Error('Could not open leave policy');
                    const blob = await res.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank', 'noopener,noreferrer');
                    URL.revokeObjectURL(blobUrl);
                  } catch (e) {
                    setFeedback(e.message);
                  }
                }}
              >
                View PDF
              </Button>
            </Stack>
          </Alert>
        )}
        {feedback && <Alert severity="info" sx={{ mb: 2 }}>{feedback}</Alert>}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Apply leave" />
          <Tab label="Outdoor duty" />
          <Tab label="My requests" />
        </Tabs>
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Leave type" value={leaveForm.leave_type}
                onChange={(e) => setLeaveForm((f) => ({ ...f, leave_type: e.target.value }))}>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="sick">Sick</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField type="date" fullWidth size="small" label="From" InputLabelProps={{ shrink: true }}
                value={leaveForm.from_date} onChange={(e) => setLeaveForm((f) => ({ ...f, from_date: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField type="date" fullWidth size="small" label="To" InputLabelProps={{ shrink: true }}
                value={leaveForm.to_date} onChange={(e) => setLeaveForm((f) => ({ ...f, to_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline minRows={2} label="Reason" value={leaveForm.reason}
                onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={submitLeave}>Submit leave</Button>
            </Grid>
          </Grid>
        )}
        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField type="date" fullWidth size="small" label="From" InputLabelProps={{ shrink: true }}
                value={odForm.from_date} onChange={(e) => setOdForm((f) => ({ ...f, from_date: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField type="date" fullWidth size="small" label="To" InputLabelProps={{ shrink: true }}
                value={odForm.to_date} onChange={(e) => setOdForm((f) => ({ ...f, to_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Location" value={odForm.location}
                onChange={(e) => setOdForm((f) => ({ ...f, location: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline minRows={2} label="Purpose" value={odForm.purpose}
                onChange={(e) => setOdForm((f) => ({ ...f, purpose: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={submitOutdoor}>Submit outdoor duty</Button>
            </Grid>
          </Grid>
        )}
        {tab === 2 && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>Leave</Typography>
            {history.leave.length === 0 ? <Typography variant="body2" color="text.secondary">No leave requests.</Typography> : null}
            {history.leave.map((r) => (
              <Box key={r.id}>
                <Typography variant="body2">
                  {r.leave_type} · {r.from_date}–{r.to_date} · <strong>{r.status}</strong>
                </Typography>
              </Box>
            ))}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>Outdoor duty</Typography>
            {history.outdoor.map((r) => (
              <Box key={r.id}>
                <Typography variant="body2">
                  {r.from_date}–{r.to_date} · <strong>{r.status}</strong>
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
    </>
  );

  if (embedded) return inner;

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
